import z from "zod";
import { addressModel } from "./core";
import {
  LenderApplicationStatus,
  LenderApplicationUnderwritingState,
  LenderTypeOfInstitution,
  LenderViewModel,
  OfferStatus,
} from "@cxm/client";

export const engagedLenderStatuses = [
  "Unacknowledged",
  "Acknowledged",
  "Expressed Interest",
  "Offer Submitted",
  "Offer Accepted",
  "Offer Rejected",
  "Missed",
  "Declined",
  "Unsubmitted",
  "Awaiting Approval",
  "Submission Denied",
  "Underwriting",
  "Requested Info",
  "Awaiting Lender Response",
];

export const filterableEngagedLenderStatuses = [
  "Unacknowledged",
  "Acknowledged",
  "Expressed Interest",
  "Underwriting",
  "Requested Info",
  "Awaiting Lender Response",
  "Offer Submitted",
  "Offer Accepted",
  "Offer Rejected",
  "Missed",
  "Declined",
];

export const lenderTypeOptions = [
  {
    label: "Local",
    value: LenderTypeOfInstitution.LOCAL,
  },
  {
    label: "Regional",
    value: LenderTypeOfInstitution.REGIONAL,
  },
  {
    label: "Credit Union",
    value: LenderTypeOfInstitution.CREDIT_UNION,
  },
  {
    label: "Private Credit",
    value: LenderTypeOfInstitution.ALTERNATIVE,
  },
  {
    label: "National",
    value: LenderTypeOfInstitution.NATIONAL,
  },
];

export type LenderTemplate = {
  TemplateName: string;
  Metrics: LenderTemplateMetric[];
};

export type LenderTemplateMetric = {
  MetricName: string;
  MetricKey: string;
  IsCustom: boolean;
  MetricOrder: number;
};

export type Covenant = {
  Id: number;
  Name: string;
};

export const isEngagedLenderInStatus: (
  s: string,
  l: LenderViewModel,
) => boolean = (
  statusTest: string,
  lender: LenderViewModel,
) => {
    if (!lender.expressedInterest) {
      lender.expressedInterest = false;
    }
    switch (statusTest) {
      case "Unsubmitted":
        return lender.status == LenderApplicationStatus.UNSUBMITTED;
      case "Awaiting Approval":
        return lender.status ==
          LenderApplicationStatus.REQUESTED_SUBMISSION_APPROVAL;
      case "Submission Denied":
        return lender.status ==
          LenderApplicationStatus.SUBMISSION_DENIED;
      case "Unacknowledged":
        return lender.status == LenderApplicationStatus.SUBMITTED &&
          !lender.expressedInterest;
      case "Expressed Interest":
        return lender.underwritingState == LenderApplicationUnderwritingState.NONE && (lender.offers?.length ?? 0) == 0 && lender.expressedInterest &&
          (lender.status == LenderApplicationStatus.SUBMITTED ||
            lender.status == LenderApplicationStatus.ACKNOWLEDGED);
      case "Underwriting":
        return lender.underwritingState == LenderApplicationUnderwritingState.IN_UNDERWRITING && (lender.offers?.length ?? 0) == 0 && lender.expressedInterest &&
          (lender.status == LenderApplicationStatus.SUBMITTED ||
            lender.status == LenderApplicationStatus.ACKNOWLEDGED);
      case "Requested Info":
        return lender.underwritingState == LenderApplicationUnderwritingState.REQUESTED_ADDITIONAL_INFO && (lender.offers?.length ?? 0) == 0 && lender.expressedInterest &&
          (lender.status == LenderApplicationStatus.SUBMITTED ||
            lender.status == LenderApplicationStatus.ACKNOWLEDGED);
      case "Awaiting Lender Response":
        return lender.underwritingState == LenderApplicationUnderwritingState.WAITING_LENDER_RESPONSE && (lender.offers?.length ?? 0) == 0 && lender.expressedInterest &&
          (lender.status == LenderApplicationStatus.SUBMITTED ||
            lender.status == LenderApplicationStatus.ACKNOWLEDGED);
      case "Offer Accepted":
        return lender.status == LenderApplicationStatus.PURSUED;
      case "Offer Submitted":
        return lender.status == LenderApplicationStatus.ACKNOWLEDGED &&
          lender.offers?.find((x) =>
            !x.isBinding &&
            (x.status == OfferStatus.SUBMITTED_BY_LENDER ||
              x.status == OfferStatus.APPROVED_BY_SPECIALIST)
          ) != undefined;
      case "Offer Rejected":
        return lender.status == LenderApplicationStatus.ACKNOWLEDGED &&
          !isEngagedLenderInStatus("Offer Submitted", lender) &&
          !isEngagedLenderInStatus("Offer Accepted", lender) &&
          lender.offers?.find((x) =>
            !x.isBinding &&
            x.status == OfferStatus.REJECTED_BY_BORROWER
          ) != undefined;
      case "Acknowledged":
        return (lender.offers?.length ?? 0) == 0 &&
          lender.status == LenderApplicationStatus.ACKNOWLEDGED &&
          !lender.expressedInterest;
      case "Missed":
        return lender.status == LenderApplicationStatus.MISSED ||
          lender.status == LenderApplicationStatus.NOT_PURSUED;
      case "Declined":
        return lender.status == LenderApplicationStatus.DECLINED;
      default:
        return false;
    }
  };

export const getEngagedLenderApplicationString = (
  status: LenderApplicationStatus,
  offerStatus: OfferStatus[],
) => {
  switch (status) {
    case LenderApplicationStatus.SUBMITTED:
      return "Unacknowledged";
    case LenderApplicationStatus.ACKNOWLEDGED:
      if (offerStatus.length > 0) {
        return "Offer Submitted";
      }
      return "Acknowledged";
    case LenderApplicationStatus.PURSUED:
      return "Offer Accepted";
    case LenderApplicationStatus.NOT_PURSUED:
      return "Offer Rejected";
    case LenderApplicationStatus.DECLINED:
      return "Declined";
    case LenderApplicationStatus.MISSED:
      return "Missed";
  }
};

export const lenderProfileModel = z.object({
  lenderName: z.string(),
  primaryContactId: z.number().nullish(),
  loanTypes: z.string().array(),
  address: addressModel,
  emailAddress: z.string().nullish(),
  altContactName: z.string().nullish(),
  altContactNumber: z.string().nullish(),
  numberOfLocations: z.number().nullish(),
  typeOfInstitution: z.string().nullish(),
  totalAssets: z.number().nullish(),
  totalBalanceSheet: z.number().nullish(),
  region: z.object({
    states: z.string().array().nullish(),
    includedZips: z.string().nullish(),
    excludedZips: z.string().nullish(),
  }).nullish(),
  businessOverview: z.string().nullish(),
});

export type LenderCriteria = z.infer<typeof lenderCriteriaModel>;

export const lenderCriteriaModel = z.object({
  ttM_Revenue_Min: z.number().nullish(),
  ttM_Revenue_Max: z.number().nullish(),
  ttM_EBITDA_Min: z.number().nullish(),
  ttM_EBITDA_Max: z.number().nullish(),
  fmV_PPE_Min: z.number().nullish(),
  fmV_PPE_Max: z.number().nullish(),
  aR_Min: z.number().nullish(),
  aR_Max: z.number().nullish(),
  inventory_Min: z.number().nullish(),
  inventory_Max: z.number().nullish(),
  years_In_Business_Min: z.number().nullish(),
  years_In_Business_Max: z.number().nullish(),

  rate_Min: z.number().nullish(),
  rate_Max: z.number().nullish(),

  rate_Spread_Min: z.number().nullish(),
  rate_Spread_Max: z.number().nullish(),
  rate_Reference: z.string().nullish(),

  maturity_Min: z.number().nullish(),
  maturity_Max: z.number().nullish(),

  equipment_Orderly_Liquidation_Min: z.number().nullish(),
  equipment_Orderly_Liquidation_Max: z.number().nullish(),
  equipment_Forced_Liquidation_Min: z.number().nullish(),
  equipment_Forced_Liquidation_Max: z.number().nullish(),

  loan_Amount_Min: z.number().nullish(),
  loan_Amount_Max: z.number().nullish(),

  personal_Guarantee: z.boolean().nullish(),
  audit_Review: z.boolean().nullish(),
  has_CFO: z.boolean().nullish(),
  external_Accountant: z.boolean().nullish(),

  loan_To_Revenue_Min: z.number().nullish(),
  loan_To_Revenue_Max: z.number().nullish(),

  loan_To_EBITDA_Min: z.number().nullish(),
  loan_To_EBITDA_Max: z.number().nullish(),

  advance_AR_Rate_Min: z.number().nullish(),
  advance_AR_Rate_Max: z.number().nullish(),

  advance_Equip_Rate_Min: z.number().nullish(),
  advance_Equip_Rate_Max: z.number().nullish(),

  advance_Inventory_Rate_Min: z.number().nullish(),
  advance_Inventory_Rate_Max: z.number().nullish(),

  advance_Cash_Rate_Min: z.number().nullish(),
  advance_Cash_Rate_Max: z.number().nullish(),

  revenue_Mixes: z.array(z.string()).nullish(),

  industries: z.array(z.string()).nullish(),

  sub_Industries: z.array(z.string()).nullish(),

  loan_Types: z.array(z.string()).nullish(),

  loan_Uses: z.array(z.string()).nullish(),
});

export const fullLenderCriteriaModel = z.object({
  DefaultCriteria: lenderCriteriaModel,
  LoanTypeOverrides: z.object({
    LoanType: z.string(),
    Criteria: lenderCriteriaModel,
  }).array(),
});

export const defaultLenderCriteria = {
  DefaultCriteria: {},
  LoanTypeOverrides: [],
};
